import type {
  StepChannel,
  StepAdapter,
  StepSnapshot,
  StepSignal,
  WizardCommand,
  Unsubscribe,
} from './types';
import { reduceSnapshot, INITIAL_SNAPSHOT } from './snapshotReducer';

export class StepChannelImpl implements StepChannel {
  readonly channelKey: string;
  readonly wizardId: string;
  readonly stepId: string;
  private _status: 'idle' | 'connected' | 'disposed' = 'idle';

  private signalHandlers = new Set<(signal: StepSignal) => void>();
  private commandHandlers = new Set<(command: WizardCommand) => void>();
  private adapter: StepAdapter | null = null;
  private snapshot: StepSnapshot = { ...INITIAL_SNAPSHOT };

  get status(): 'idle' | 'connected' | 'disposed' {
    return this._status;
  }

  constructor(wizardId: string, stepId: string) {
    this.wizardId = wizardId;
    this.stepId = stepId;
    this.channelKey = `${wizardId}:${stepId}`;
  }

  // ── Wizard side ────────────────────────────────────────

  send(command: WizardCommand): void {
    if (this._status === 'disposed') return;
    if (this._status !== 'connected') {
      console.warn(`[Channel:${this.channelKey}] Command sent before connect`);
      return;
    }
    this.commandHandlers.forEach((h) => h(command));
  }

  onSignal(handler: (signal: StepSignal) => void): Unsubscribe {
    this.signalHandlers.add(handler);
    return () => this.signalHandlers.delete(handler);
  }

  getSnapshot(): StepSnapshot {
    return { ...this.snapshot };
  }

  // ── Step content side ──────────────────────────────────

  connect(adapter: StepAdapter): void {
    if (this._status === 'disposed') return;
    this.adapter = adapter;
    this._status = 'connected';
    this.commandHandlers.add((cmd) => adapter.handleCommand(cmd));
  }

  onCommand(handler: (command: WizardCommand) => void): Unsubscribe {
    this.commandHandlers.add(handler);
    return () => this.commandHandlers.delete(handler);
  }

  emit(signal: StepSignal): void {
    if (this._status === 'disposed') return;
    this.snapshot = reduceSnapshot(this.snapshot, signal);
    this.signalHandlers.forEach((h) => h(signal));
  }

  // ── Teardown ──────────────────────────────────────────

  dispose(): void {
    if (this._status === 'disposed') return;
    this.adapter?.destroy();
    this.adapter = null;
    this.signalHandlers.clear();
    this.commandHandlers.clear();
    this._status = 'disposed';
  }
}
